import { useEffect, useMemo, useState } from 'react'
import {
  ArrowDown,
  ArrowUp,
  Bike,
  Building2,
  Car,
  Check,
  Copy,
  ExternalLink,
  Footprints,
  GripVertical,
  Link2,
  LoaderCircle,
  MapPin,
  Mountain,
  Plane,
  Plus,
  Route,
  Search,
  Train,
  Trash2,
} from 'lucide-react'
import { supabase } from '../lib/supabase'
import { computeGoogleRoute, inferGoogleTravelMode, isGoogleMapsDirectionsUrl, isGoogleRoutesConfigured } from '../lib/googleRoutes'
import { PlaceSearchError, SEARCH_TYPES, flagFromCountryCode, searchPlaces as runPlaceSearch } from '../lib/placeSearch'

const EMPTY_STOP = {
  label: '',
  kind: 'base',
  latitude: '',
  longitude: '',
}

const GOOGLE_TRAVEL_MODES = [
  { value: 'DRIVING', label: 'Drive', icon: Car },
  { value: 'TRANSIT', label: 'Train / transit', icon: Train },
  { value: 'WALKING', label: 'Walk', icon: Footprints },
  { value: 'BICYCLING', label: 'Cycle', icon: Bike },
]

function legKey(from, to) {
  return `${from.id}:${to.id}`
}

function formatDistance(metres) {
  const value = Number(metres || 0)
  if (!value) return ''
  return value >= 1000 ? `${(value / 1000).toFixed(value >= 100000 ? 0 : 1)} km` : `${Math.round(value)} m`
}

function formatDuration(seconds) {
  const minutes = Math.round(Number(seconds || 0) / 60)
  if (!minutes) return ''
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const remaining = minutes % 60
  return `${hours}h${remaining ? ` ${remaining}m` : ''}`
}

function normalise(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '')
}

const WEB_MERCATOR_LAT_LIMIT = 85.051129

function clampLatitude(latitude) {
  return Math.max(-WEB_MERCATOR_LAT_LIMIT, Math.min(WEB_MERCATOR_LAT_LIMIT, latitude))
}

function toMercator([longitude, latitude]) {
  const lat = clampLatitude(latitude) * Math.PI / 180
  return [
    (longitude + 180) / 360,
    (1 - Math.log(Math.tan(lat) + (1 / Math.cos(lat))) / Math.PI) / 2,
  ]
}

function fromMercator([x, y]) {
  const longitude = x * 360 - 180
  const n = Math.PI - 2 * Math.PI * y
  const latitude = 180 / Math.PI * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)))
  return [longitude, latitude]
}

function buildManualArc(from, to, bendValue) {
  const start = toMercator([Number(from.longitude), Number(from.latitude)])
  const end = toMercator([Number(to.longitude), Number(to.latitude)])

  if (Math.abs(end[0] - start[0]) > 0.5) end[0] += end[0] > start[0] ? -1 : 1

  const dx = end[0] - start[0]
  const dy = end[1] - start[1]
  const length = Math.hypot(dx, dy)
  if (!Number.isFinite(length) || length === 0) {
    return [[Number(from.longitude), Number(from.latitude)], [Number(to.longitude), Number(to.latitude)]]
  }

  const midpoint = [(start[0] + end[0]) / 2, (start[1] + end[1]) / 2]
  const perpendicular = [-dy / length, dx / length]
  const signedBend = Math.max(-100, Math.min(100, Number(bendValue) || 0)) / 100
  const offset = Math.min(length * 0.28, 0.045) * signedBend
  const control = [
    midpoint[0] + perpendicular[0] * offset,
    midpoint[1] + perpendicular[1] * offset,
  ]

  const samples = Math.max(16, Math.min(72, Math.ceil(length * 1200)))
  const points = []
  for (let index = 0; index <= samples; index += 1) {
    const t = index / samples
    const inverse = 1 - t
    let x = inverse * inverse * start[0] + 2 * inverse * t * control[0] + t * t * end[0]
    const y = inverse * inverse * start[1] + 2 * inverse * t * control[1] + t * t * end[1]
    x = ((x % 1) + 1) % 1
    points.push(fromMercator([x, y]))
  }
  return points
}

function savedArcBend(leg) {
  if (leg?.source !== 'manual') return 0
  const match = String(leg.source_url || '').match(/^arc:([-+]?\d+(?:\.\d+)?)$/)
  return match ? Number(match[1]) : 0
}

function sameCoordinate(point, summit) {
  return Math.abs(Number(point.latitude) - Number(summit.latitude)) < 0.0001
    && Math.abs(Number(point.longitude) - Number(summit.longitude)) < 0.0001
}

function pointMatchesSummit(point, summit) {
  return point.kind === 'summit'
    && (sameCoordinate(point, summit) || normalise(point.label) === normalise(summit.peak_name))
}

function kindIcon(kind, size = 16) {
  if (kind === 'airport') return <Plane size={size} />
  if (kind === 'summit') return <Mountain size={size} />
  if (kind === 'city') return <Building2 size={size} />
  return <MapPin size={size} />
}




export default function RouteBuilder({
  trips,
  lockedTripId = null,
  session = null,
  summits,
  routePoints,
  routeLegs = [],
  routeLegsReady = false,
  routeLegsError = '',
  busy,
  setBusy,
  setNotice,
  refresh,
}) {
  const [ownTripId, setOwnTripId] = useState('')
  // When embedded in the expedition editor the trip is chosen upstream.
  const selectedTripId = lockedTripId || ownTripId
  const setSelectedTripId = setOwnTripId
  const [stopForm, setStopForm] = useState(EMPTY_STOP)
  const [draft, setDraft] = useState([])
  const [draggedId, setDraggedId] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchType, setSearchType] = useState('all')
  const [searchResults, setSearchResults] = useState([])
  const [searching, setSearching] = useState(false)
  const [searchError, setSearchError] = useState('')
  const [selectedResultKey, setSelectedResultKey] = useState('')
  const [legForms, setLegForms] = useState({})
  const [legOptions, setLegOptions] = useState({})
  const [importingLegKey, setImportingLegKey] = useState('')
  const [savingLegKey, setSavingLegKey] = useState('')
  const [arcForms, setArcForms] = useState({})
  const [alsoTrackSummit, setAlsoTrackSummit] = useState(false)
  const [summitExtra, setSummitExtra] = useState({
    country_name: '', country_code: '', flag: '', elevation_m: '', status: 'planned', summit_date: '',
  })

  useEffect(() => {
    if (lockedTripId) return
    if (!trips.length) {
      setSelectedTripId('')
      return
    }
    if (!trips.some((trip) => trip.id === selectedTripId)) {
      setSelectedTripId(trips[0].id)
    }
  }, [trips, selectedTripId, lockedTripId])

  const selectedTrip = useMemo(
    () => trips.find((trip) => trip.id === selectedTripId) || null,
    [trips, selectedTripId],
  )

  const selectedRoute = useMemo(
    () => routePoints
      .filter((point) => point.trip_id === selectedTripId)
      .sort((a, b) => Number(a.position) - Number(b.position)),
    [routePoints, selectedTripId],
  )

  useEffect(() => {
    setDraft(selectedRoute)
  }, [selectedRoute])

  useEffect(() => {
    setSearchResults([])
    setSearchError('')
    setSelectedResultKey('')
    setStopForm(EMPTY_STOP)
    setLegOptions({})
    setArcForms({})
  }, [selectedTripId])

  const tripSummits = useMemo(
    () => summits.filter((summit) => summit.trip_id === selectedTripId),
    [summits, selectedTripId],
  )

  const availableSummits = useMemo(
    () => tripSummits.filter((summit) => !selectedRoute.some((point) => pointMatchesSummit(point, summit))),
    [tripSummits, selectedRoute],
  )

  const exactLegByPoints = useMemo(() => new Map(
    routeLegs
      .filter((leg) => leg.trip_id === selectedTripId)
      .map((leg) => [`${leg.from_point_id}:${leg.to_point_id}`, leg]),
  ), [routeLegs, selectedTripId])

  function legForm(from, to) {
    const key = legKey(from, to)
    const existing = exactLegByPoints.get(key)
    return legForms[key] || {
      url: existing?.source_url || '',
      mode: existing?.travel_mode || 'DRIVING',
      departureTime: '',
    }
  }

  function updateLegForm(from, to, patch) {
    const key = legKey(from, to)
    setLegForms((current) => ({
      ...current,
      [key]: { ...legForm(from, to), ...patch },
    }))
    setLegOptions((current) => {
      if (!current[key]) return current
      const next = { ...current }
      delete next[key]
      return next
    })
  }


  function arcBend(from, to) {
    const key = legKey(from, to)
    if (Object.prototype.hasOwnProperty.call(arcForms, key)) return arcForms[key]
    return savedArcBend(exactLegByPoints.get(key))
  }

  function updateArcBend(from, to, value) {
    const key = legKey(from, to)
    setArcForms((current) => ({ ...current, [key]: Number(value) }))
  }

  function nextPosition() {
    return Math.max(0, ...selectedRoute.map((point) => Number(point.position) || 0)) + 1
  }

  async function searchPlaces(event) {
    event.preventDefault()
    setSearching(true)
    setSearchError('')
    setSearchResults([])

    try {
      const results = await runPlaceSearch(searchQuery, searchType)
      setSearchResults(results)
      if (!results.length) setSearchError('No matching places found. Try adding the country name or use manual coordinates.')
    } catch (error) {
      setSearchError(error instanceof PlaceSearchError
        ? error.message
        : 'Place search is unavailable right now. You can still enter coordinates manually.')
    } finally {
      setSearching(false)
    }
  }

  function chooseSearchResult(result) {
    setSelectedResultKey(result.key)
    setStopForm({
      label: result.label,
      kind: result.kind,
      latitude: result.latitude.toFixed(6),
      longitude: result.longitude.toFixed(6),
    })
    // A peak is usually both a stop and a summit target, so offer to create
    // both from one search instead of retyping it under Summits.
    setAlsoTrackSummit(result.kind === 'summit')
    setSummitExtra({
      country_name: result.countryName || '',
      country_code: result.countryCode || '',
      flag: flagFromCountryCode(result.countryCode),
      elevation_m: result.elevation ? String(result.elevation) : '',
      status: 'planned',
      summit_date: '',
    })
  }

  async function addSummit(summit) {
    if (!supabase || !selectedTripId || busy) return
    setBusy(true)
    setNotice('')
    const { error } = await supabase.from('route_points').insert({
      trip_id: selectedTripId,
      position: nextPosition(),
      label: summit.peak_name,
      kind: 'summit',
      latitude: Number(summit.latitude),
      longitude: Number(summit.longitude),
    })
    setBusy(false)
    setNotice(error ? error.message : `${summit.peak_name} added to ${selectedTrip?.name || 'route'}.`)
    if (!error) await refresh()
  }

  async function addAllSummits() {
    if (!supabase || !selectedTripId || !availableSummits.length || busy) return
    setBusy(true)
    setNotice('')
    const firstPosition = nextPosition()
    const rows = availableSummits.map((summit, index) => ({
      trip_id: selectedTripId,
      position: firstPosition + index,
      label: summit.peak_name,
      kind: 'summit',
      latitude: Number(summit.latitude),
      longitude: Number(summit.longitude),
    }))
    const { error } = await supabase.from('route_points').insert(rows)
    setBusy(false)
    setNotice(error ? error.message : `${rows.length} summit${rows.length === 1 ? '' : 's'} added to the route.`)
    if (!error) await refresh()
  }

  async function addCustomStop(event) {
    event.preventDefault()
    if (!supabase || !selectedTripId || busy) return
    setBusy(true)
    setNotice('')

    const label = stopForm.label.trim()
    const latitude = Number(stopForm.latitude)
    const longitude = Number(stopForm.longitude)

    // Optionally create the summit row too, so a peak is entered once rather
    // than twice. route_points.summit_id links the two; without the migration
    // the insert still works, it just isn't linked.
    let summitId = null
    if (alsoTrackSummit && stopForm.kind === 'summit') {
      const { data, error: summitError } = await supabase
        .from('summits')
        .insert({
          owner_id: session?.user?.id || null,
          trip_id: selectedTripId,
          country_code: summitExtra.country_code || null,
          country_name: summitExtra.country_name || label,
          flag: summitExtra.flag || null,
          peak_name: label,
          elevation_m: Number(summitExtra.elevation_m) || 0,
          latitude,
          longitude,
          status: summitExtra.status || 'planned',
          summit_date: summitExtra.summit_date || null,
          scenic_choice: false,
        })
        .select('id')
        .single()
      if (summitError) {
        setBusy(false)
        setNotice(`Stop not added — the summit could not be created: ${summitError.message}`)
        return
      }
      summitId = data?.id || null
    }

    const stopPayload = {
      trip_id: selectedTripId,
      position: nextPosition(),
      label,
      kind: stopForm.kind,
      latitude,
      longitude,
    }
    if (summitId) stopPayload.summit_id = summitId

    const { error } = await supabase.from('route_points').insert(stopPayload)
    setBusy(false)
    setNotice(error
      ? error.message
      : `${label} added to the route${summitId ? ' and tracked as a summit target' : ''}.`)
    if (!error) {
      setStopForm(EMPTY_STOP)
      setSelectedResultKey('')
      setAlsoTrackSummit(false)
      await refresh()
    }
  }

  // One transactional RPC instead of 2N round trips. The old client-side
  // version wrote every stop twice (park at a temporary position, then settle),
  // so a nine-stop reorder meant eighteen requests — and eighteen realtime
  // events. It also left rows parked in the 100000+ range if it failed halfway.
  // The client-side path is kept as a fallback for when the function is absent.
  async function saveOrderToDatabase(nextDraft) {
    const { error } = await supabase.rpc('reorder_route_points', {
      p_trip_id: selectedTripId,
      p_ids: nextDraft.map((point) => point.id),
    })
    if (!error) return

    const missingFunction = error.code === 'PGRST202'
      || String(error.message || '').toLowerCase().includes('reorder_route_points')
    if (!missingFunction) throw error

    const temporaryBase = 100000 + Math.floor(Math.random() * 100000)
    const temporaryUpdates = await Promise.all(
      nextDraft.map((point, index) => supabase
        .from('route_points')
        .update({ position: temporaryBase + index })
        .eq('id', point.id)),
    )
    const temporaryError = temporaryUpdates.find((result) => result.error)?.error
    if (temporaryError) throw temporaryError

    const finalUpdates = await Promise.all(
      nextDraft.map((point, index) => supabase
        .from('route_points')
        .update({ position: index + 1 })
        .eq('id', point.id)),
    )
    const finalError = finalUpdates.find((result) => result.error)?.error
    if (finalError) throw finalError
  }

  async function persistOrder(nextDraft) {
    if (!supabase || busy || nextDraft.length < 2) return
    setDraft(nextDraft)
    setBusy(true)
    setNotice('Saving route order…')

    try {
      await saveOrderToDatabase(nextDraft)
      setNotice('Route order saved.')
      await refresh()
    } catch (error) {
      setNotice(error.message || 'Could not save route order.')
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  function moveItem(fromIndex, toIndex) {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || toIndex >= draft.length) return
    const next = [...draft]
    const [moved] = next.splice(fromIndex, 1)
    next.splice(toIndex, 0, moved)
    persistOrder(next)
  }

  function dropOn(targetId) {
    if (!draggedId || draggedId === targetId) return
    const fromIndex = draft.findIndex((point) => point.id === draggedId)
    const toIndex = draft.findIndex((point) => point.id === targetId)
    setDraggedId(null)
    moveItem(fromIndex, toIndex)
  }

  async function findGoogleLegOptions(from, to) {
    if (!supabase || busy) return
    const key = legKey(from, to)
    const form = legForm(from, to)

    if (!routeLegsReady) {
      setNotice(routeLegsError || 'Run the route legs database upgrade first.')
      return
    }
    if (!isGoogleRoutesConfigured) {
      setNotice('Add VITE_GOOGLE_MAPS_API_KEY to .env.local, then restart Summit Hub.')
      return
    }
    if (form.url && !isGoogleMapsDirectionsUrl(form.url)) {
      setNotice('That does not look like a Google Maps directions link.')
      return
    }

    const travelMode = inferGoogleTravelMode(form.url, form.mode)
    setImportingLegKey(key)
    setBusy(true)
    setNotice(`Getting ${travelMode.toLowerCase()} route options from Google…`)

    try {
      const options = await computeGoogleRoute({
        from,
        to,
        travelMode,
        departureTime: form.departureTime || null,
      })
      setLegOptions((current) => ({
        ...current,
        [key]: { options, travelMode },
      }))
      setNotice(`Google returned ${options.length} route option${options.length === 1 ? '' : 's'}. Choose the one that matches the highlighted route in Google Maps.`)
    } catch (error) {
      setNotice(error.message || 'Google could not calculate this route.')
    } finally {
      setImportingLegKey('')
      setBusy(false)
    }
  }

  async function saveGoogleLegOption(from, to, selected, travelMode) {
    if (!supabase || busy) return
    const key = legKey(from, to)
    const form = legForm(from, to)
    setSavingLegKey(`${key}:${selected.index}`)
    setBusy(true)
    setNotice(`Saving route option ${selected.index + 1}…`)

    try {
      const { error } = await supabase.from('route_legs').upsert({
        trip_id: selectedTripId,
        from_point_id: from.id,
        to_point_id: to.id,
        travel_mode: travelMode,
        source: 'google',
        source_url: form.url.trim(),
        coordinates: selected.coordinates,
        distance_m: selected.distance_m || null,
        duration_seconds: selected.duration_seconds || null,
      }, { onConflict: 'trip_id,from_point_id,to_point_id' })
      if (error) throw error
      setLegOptions((current) => {
        const next = { ...current }
        delete next[key]
        return next
      })
      setNotice(`${from.label} → ${to.label} now uses Google route option ${selected.index + 1}.`)
      await refresh()
    } catch (error) {
      setNotice(error.message || 'Could not save this Google route.')
    } finally {
      setSavingLegKey('')
      setBusy(false)
    }
  }

  async function saveManualArc(from, to) {
    if (!supabase || busy || !routeLegsReady) return
    const key = legKey(from, to)
    const bend = arcBend(from, to)
    setBusy(true)
    setNotice(`Saving custom arc for ${from.label} → ${to.label}…`)

    try {
      const { error } = await supabase.from('route_legs').upsert({
        trip_id: selectedTripId,
        from_point_id: from.id,
        to_point_id: to.id,
        travel_mode: 'OTHER',
        source: 'manual',
        source_url: `arc:${bend}`,
        coordinates: buildManualArc(from, to, bend),
        distance_m: null,
        duration_seconds: null,
      }, { onConflict: 'trip_id,from_point_id,to_point_id' })
      if (error) throw error
      setNotice(`${from.label} → ${to.label} now uses your custom arc.`)
      await refresh()
    } catch (error) {
      setNotice(error.message || 'Could not save this custom arc.')
    } finally {
      setBusy(false)
    }
  }

  async function removeExactLeg(leg, from, to) {
    if (!supabase || busy || !window.confirm(`Return ${from.label} → ${to.label} to a gentle map arc?`)) return
    setBusy(true)
    const { error } = await supabase.from('route_legs').delete().eq('id', leg.id)
    setBusy(false)
    setNotice(error ? error.message : `${from.label} → ${to.label} is using the gentle arc again.`)
    if (!error) await refresh()
  }

  async function duplicateStop(point, index) {
    if (!supabase || busy) return
    setBusy(true)
    setNotice(`Duplicating ${point.label}…`)

    try {
      const { data: duplicate, error } = await supabase
        .from('route_points')
        .insert({
          trip_id: selectedTripId,
          position: nextPosition(),
          label: point.label,
          kind: point.kind,
          latitude: Number(point.latitude),
          longitude: Number(point.longitude),
        })
        .select()
        .single()
      if (error) throw error

      const next = [...draft]
      next.splice(index + 1, 0, duplicate)
      setDraft(next)
      await saveOrderToDatabase(next)
      setNotice(`${point.label} duplicated directly after the original.`)
      await refresh()
    } catch (error) {
      setNotice(error.message || `Could not duplicate ${point.label}.`)
      await refresh()
    } finally {
      setBusy(false)
    }
  }

  async function deleteStop(point) {
    if (!supabase || busy || !window.confirm(`Remove ${point.label} from this route?`)) return
    setBusy(true)
    setNotice('')
    const { error } = await supabase.from('route_points').delete().eq('id', point.id)
    if (error) {
      setBusy(false)
      setNotice(error.message)
      return
    }

    const remaining = draft.filter((item) => item.id !== point.id)
    setBusy(false)
    setNotice(`${point.label} removed.`)
    await refresh()
    if (remaining.length > 1) await persistOrder(remaining)
  }

  return (
    <div className="route-builder">
      {!lockedTripId && (
        <section className="route-builder__top">
          <div>
            <div className="form-title"><Route size={18} /> Build a route</div>
            <p>Select one trip, add its existing summit targets, search for other places, then drag everything into the exact journey order.</p>
          </div>
          <label className="route-trip-select">Trip
            <select value={selectedTripId} onChange={(event) => setSelectedTripId(event.target.value)}>
              {trips.map((trip) => <option key={trip.id} value={trip.id}>{trip.name}</option>)}
            </select>
          </label>
        </section>
      )}

      {!selectedTrip ? (
        <div className="route-empty">Create a trip first.</div>
      ) : (
        <div className="route-builder__grid">
          <div className="route-builder__tools">
            <section className="route-tool-card">
              <div className="route-tool-card__head">
                <div>
                  <strong>Summit targets</strong>
                  <small>Uses peaks you have already entered under Summits.</small>
                </div>
                {availableSummits.length > 1 && (
                  <button type="button" className="secondary-button" onClick={addAllSummits} disabled={busy}>Add all</button>
                )}
              </div>

              <div className="summit-picker-list">
                {tripSummits.length === 0 && <p className="route-helper">No summits are assigned to this trip yet.</p>}
                {tripSummits.map((summit) => {
                  const alreadyAdded = !availableSummits.some((item) => item.id === summit.id)
                  return (
                    <div className={`summit-picker ${alreadyAdded ? 'is-added' : ''}`} key={summit.id}>
                      <span className="summit-picker__flag">{summit.flag || '🏔️'}</span>
                      <span><strong>{summit.peak_name}</strong><small>{summit.country_name} · {Number(summit.elevation_m).toLocaleString()}m</small></span>
                      <button type="button" onClick={() => addSummit(summit)} disabled={busy || alreadyAdded}>
                        {alreadyAdded ? 'Added' : <><Plus size={14} /> Add</>}
                      </button>
                    </div>
                  )
                })}
              </div>
            </section>

            <section className="route-tool-card place-search-card">
              <div className="route-tool-card__head">
                <div>
                  <strong>Search for a stop</strong>
                  <small>Find airports, cities, bases and mapped peaks without looking up coordinates.</small>
                </div>
              </div>

              <form className="place-search" onSubmit={searchPlaces}>
                <div className="place-search__bar">
                  <Search size={16} />
                  <input
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="e.g. Málaga Airport, Capileira, Mount Etna"
                    aria-label="Search for a place"
                  />
                  <button type="submit" className="primary-button" disabled={searching}>
                    {searching ? <LoaderCircle className="spin" size={15} /> : 'Search'}
                  </button>
                </div>
                <div className="place-search__filters" aria-label="Place type">
                  {SEARCH_TYPES.map((type) => (
                    <button
                      type="button"
                      key={type.value}
                      className={searchType === type.value ? 'active' : ''}
                      onClick={() => setSearchType(type.value)}
                    >
                      {type.label}
                    </button>
                  ))}
                </div>
              </form>

              {searchError && <p className="place-search__error">{searchError}</p>}

              {searchResults.length > 0 && (
                <div className="place-results" aria-label="Place search results">
                  {searchResults.map((result) => (
                    <button
                      type="button"
                      key={result.key}
                      className={selectedResultKey === result.key ? 'selected' : ''}
                      onClick={() => chooseSearchResult(result)}
                    >
                      <span className={`route-kind route-kind--${result.kind}`}>{kindIcon(result.kind, 15)}</span>
                      <span><strong>{result.label}</strong><small>{result.context || `${result.latitude.toFixed(4)}, ${result.longitude.toFixed(4)}`}</small></span>
                      {selectedResultKey === result.key && <Check size={15} />}
                    </button>
                  ))}
                </div>
              )}

              <form className="route-custom-form searched-stop-form" onSubmit={addCustomStop}>
                <div className="selected-stop-title">
                  <strong>{stopForm.label ? 'Selected stop' : 'Manual stop'}</strong>
                  <small>{stopForm.label ? 'Check the details, then add it to the end of the route.' : 'Search above, or enter a place manually.'}</small>
                </div>
                <label>Label<input value={stopForm.label} onChange={(event) => setStopForm({ ...stopForm, label: event.target.value })} placeholder="e.g. Málaga Airport" required /></label>
                <div className="form-row">
                  <label>Kind<select value={stopForm.kind} onChange={(event) => setStopForm({ ...stopForm, kind: event.target.value })}><option value="airport">Airport</option><option value="base">Base</option><option value="city">City</option><option value="summit">Summit / peak</option><option value="other">Other</option></select></label>
                  <label>Latitude<input type="number" step="any" value={stopForm.latitude} onChange={(event) => setStopForm({ ...stopForm, latitude: event.target.value })} required /></label>
                </div>
                <label>Longitude<input type="number" step="any" value={stopForm.longitude} onChange={(event) => setStopForm({ ...stopForm, longitude: event.target.value })} required /></label>

                {stopForm.kind === 'summit' && (
                  <div className="stop-summit-link">
                    <label className="map-toggle stop-summit-link__toggle">
                      <input
                        type="checkbox"
                        checked={alsoTrackSummit}
                        onChange={(event) => setAlsoTrackSummit(event.target.checked)}
                      />
                      <span>Also track as a summit target</span>
                    </label>
                    {alsoTrackSummit && (
                      <>
                        <div className="form-row">
                          <label>Country<input value={summitExtra.country_name} onChange={(event) => setSummitExtra({ ...summitExtra, country_name: event.target.value })} required /></label>
                          <label>Flag<input value={summitExtra.flag} onChange={(event) => setSummitExtra({ ...summitExtra, flag: event.target.value })} placeholder="🇪🇸" /></label>
                        </div>
                        <div className="form-row">
                          <label>Code<input value={summitExtra.country_code} maxLength="2" onChange={(event) => setSummitExtra({ ...summitExtra, country_code: event.target.value.toUpperCase() })} /></label>
                          <label>Elevation (m)<input type="number" value={summitExtra.elevation_m} onChange={(event) => setSummitExtra({ ...summitExtra, elevation_m: event.target.value })} /></label>
                        </div>
                        <div className="form-row">
                          <label>Status<select value={summitExtra.status} onChange={(event) => setSummitExtra({ ...summitExtra, status: event.target.value })}><option value="planned">Planned</option><option value="future">Future</option><option value="completed">Completed</option></select></label>
                          <label>Date<input type="date" value={summitExtra.summit_date} onChange={(event) => setSummitExtra({ ...summitExtra, summit_date: event.target.value })} /></label>
                        </div>
                      </>
                    )}
                  </div>
                )}

                <button className="primary-button" disabled={busy}><Plus size={15} /> Add to end</button>
              </form>

              <p className="place-attribution">Place search uses OpenStreetMap data through Photon. Search availability is best-effort; manual entry remains available.</p>
            </section>
          </div>

          <section className="route-sequence-card">
            <div className="route-sequence-card__head">
              <div>
                <strong>{selectedTrip.name}</strong>
                <small>{draft.length} stop{draft.length === 1 ? '' : 's'} · drag to reorder, or use the arrows</small>
              </div>
            </div>

            <div className="route-sequence" aria-label={`${selectedTrip.name} route order`}>
              {draft.length === 0 && <div className="route-empty">Add a summit or searched stop to start this route.</div>}
              {draft.map((point, index) => (
                <div
                  key={point.id}
                  className={`route-sequence-row ${draggedId === point.id ? 'is-dragging' : ''}`}
                  draggable={!busy}
                  onDragStart={() => setDraggedId(point.id)}
                  onDragEnd={() => setDraggedId(null)}
                  onDragOver={(event) => event.preventDefault()}
                  onDrop={() => dropOn(point.id)}
                >
                  <button type="button" className="route-drag-handle" aria-label={`Drag ${point.label}`} disabled={busy}><GripVertical size={18} /></button>
                  <span className="route-position">{index + 1}</span>
                  <span className={`route-kind route-kind--${point.kind}`}>{kindIcon(point.kind)}</span>
                  <span className="route-sequence-row__copy">
                    <strong>{point.label}</strong>
                    <small>{point.kind} · {Number(point.latitude).toFixed(4)}, {Number(point.longitude).toFixed(4)}</small>
                  </span>
                  <span className="route-row-actions">
                    <button type="button" className="route-mini-button" onClick={() => moveItem(index, index - 1)} disabled={busy || index === 0} aria-label={`Move ${point.label} up`}><ArrowUp size={14} /></button>
                    <button type="button" className="route-mini-button" onClick={() => moveItem(index, index + 1)} disabled={busy || index === draft.length - 1} aria-label={`Move ${point.label} down`}><ArrowDown size={14} /></button>
                    <button type="button" className="route-mini-button" onClick={() => duplicateStop(point, index)} disabled={busy} aria-label={`Duplicate ${point.label}`} title="Duplicate stop"><Copy size={14} /></button>
                    <button type="button" className="route-mini-button route-mini-button--danger" onClick={() => deleteStop(point)} disabled={busy} aria-label={`Delete ${point.label}`}><Trash2 size={14} /></button>
                  </span>
                </div>
              ))}
            </div>

            <div className="route-leg-editor">
              <div className="route-leg-editor__head">
                <div>
                  <strong>Exact Google paths</strong>
                  <small>Flights can stay as gentle arcs. Import road, rail, walking or cycling legs so they follow the real route.</small>
                </div>
                <span>{routeLegsReady ? 'Ready' : 'Setup needed'}</span>
              </div>

              {!routeLegsReady && (
                <div className="route-upgrade-note">
                  <strong>One database upgrade needed</strong>
                  <small>{routeLegsError || 'Run supabase/route-legs-upgrade.sql in the Supabase SQL Editor.'}</small>
                </div>
              )}

              {routeLegsReady && !isGoogleRoutesConfigured && (
                <div className="route-upgrade-note">
                  <strong>Google key not added yet</strong>
                  <small>Add VITE_GOOGLE_MAPS_API_KEY to .env.local and restart the site.</small>
                </div>
              )}

              {draft.length < 2 && <div className="route-empty">Add at least two stops before importing a route.</div>}
              {draft.slice(0, -1).map((from, index) => {
                const to = draft[index + 1]
                const key = legKey(from, to)
                const existing = exactLegByPoints.get(key)
                const form = legForm(from, to)
                const optionSet = legOptions[key]
                const ModeIcon = GOOGLE_TRAVEL_MODES.find((mode) => mode.value === form.mode)?.icon || Car
                const isManualArc = existing?.source === 'manual'
                const bend = arcBend(from, to)
                const meta = existing
                  ? isManualArc
                    ? `Custom arc · bend ${savedArcBend(existing)}`
                    : [formatDistance(existing.distance_m), formatDuration(existing.duration_seconds)].filter(Boolean).join(' · ')
                  : 'Automatic gentle arc'

                return (
                  <article className={`route-leg-row ${existing ? 'is-exact' : ''}`} key={key}>
                    <div className="route-leg-row__title">
                      <span>{index + 1}</span>
                      <div><strong>{from.label} → {to.label}</strong><small>{existing ? (isManualArc ? meta : `${existing.travel_mode.toLowerCase()} · ${meta || 'exact path saved'}`) : meta}</small></div>
                      <em>{isManualArc ? 'Custom arc' : existing ? 'Exact' : 'Arc'}</em>
                    </div>
                    {(!existing || isManualArc) && (
                      <div className="manual-arc-editor">
                        <div className="manual-arc-editor__head">
                          <span>Arc bend</span>
                          <strong>{bend > 0 ? `+${bend}` : bend}</strong>
                        </div>
                        <input
                          type="range"
                          min="-100"
                          max="100"
                          step="5"
                          value={bend}
                          onChange={(event) => updateArcBend(from, to, event.target.value)}
                          aria-label={`Arc bend for ${from.label} to ${to.label}`}
                        />
                        <div className="manual-arc-editor__actions">
                          <small>Left / right changes which side the midpoint bends toward.</small>
                          <button type="button" className="secondary-button" onClick={() => saveManualArc(from, to)} disabled={busy || !routeLegsReady}>Save custom arc</button>
                        </div>
                      </div>
                    )}
                    <div className="route-leg-controls">
                      <label>Mode
                        <select value={form.mode} onChange={(event) => updateLegForm(from, to, { mode: event.target.value })}>
                          {GOOGLE_TRAVEL_MODES.map((mode) => <option key={mode.value} value={mode.value}>{mode.label}</option>)}
                        </select>
                      </label>
                      {form.mode === 'TRANSIT' && <label>Departure (optional)<input type="datetime-local" value={form.departureTime} onChange={(event) => updateLegForm(from, to, { departureTime: event.target.value })} /></label>}
                    </div>
                    <label className="google-link-field">Google Maps directions link
                      <span><Link2 size={15} /><input value={form.url} onChange={(event) => updateLegForm(from, to, { url: event.target.value, mode: inferGoogleTravelMode(event.target.value, form.mode) })} placeholder="Paste a Google Maps directions link" /></span>
                    </label>
                    <p className="google-link-note">The link helps preserve the mode and gives you a reference to open. Google does not expose which coloured alternative you clicked, so Summit Hub shows the returned options below for you to choose.</p>
                    <div className="route-leg-actions">
                      <button type="button" className="primary-button" onClick={() => findGoogleLegOptions(from, to)} disabled={busy || !routeLegsReady || !isGoogleRoutesConfigured}>
                        {importingLegKey === key ? <LoaderCircle className="spin" size={15} /> : <ModeIcon size={15} />} {existing ? 'Find new options' : 'Find route options'}
                      </button>
                      {form.url && <a className="secondary-button" href={form.url} target="_blank" rel="noreferrer"><ExternalLink size={14} /> Open link</a>}
                      {existing && <button type="button" className="text-button route-remove-path" onClick={() => removeExactLeg(existing, from, to)} disabled={busy}>{isManualArc ? 'Use automatic arc' : 'Use arc instead'}</button>}
                    </div>
                    {optionSet?.options?.length > 0 && (
                      <div className="google-route-options">
                        <div className="google-route-options__head">
                          <strong>Choose the matching route</strong>
                          <small>For transit, match the duration shown in Google Maps and use the same departure date/time above.</small>
                        </div>
                        {optionSet.options.map((option) => {
                          const optionMeta = [
                            option.duration_text || formatDuration(option.duration_seconds),
                            option.distance_text || formatDistance(option.distance_m),
                          ].filter(Boolean).join(' · ')
                          const isRecommended = option.index === 0 || option.route_labels?.includes('DEFAULT_ROUTE')
                          const saveKey = `${key}:${option.index}`
                          return (
                            <button
                              type="button"
                              className="google-route-option"
                              key={option.index}
                              onClick={() => saveGoogleLegOption(from, to, option, optionSet.travelMode)}
                              disabled={busy}
                            >
                              <span className="google-route-option__number">{option.index + 1}</span>
                              <span className="google-route-option__copy">
                                <strong>{optionMeta || `Route option ${option.index + 1}`}</strong>
                                <small>{option.description || (isRecommended ? 'Google recommended route' : `Alternative route ${option.index + 1}`)}</small>
                              </span>
                              <span className="google-route-option__action">
                                {savingLegKey === saveKey ? <LoaderCircle className="spin" size={14} /> : <Check size={14} />}
                                Use this
                              </span>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </article>
                )
              })}
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
